import EditableCell, { EditableCellProps } from "@components/EditableCell"
import {
  Header,
  getPropertiesHeaders,
  getPropertiesRows,
} from "@features/graph/details/components/PropertiesTable/helpers"
import { selectedPropertiesRowAtom } from "@features/builder/NodeDrawer/NodeForm/propertiesTableAtom"
import {
  FormHelperText,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { GraphNodeType } from "@custom-types/graphs"
import { NodeType } from "@features/builder/graph/types"
import type { ValidationError } from "@features/builder/graph/nodes/validation/types"
import { Control, FieldValues, Path } from "react-hook-form"
import { useAtom } from "jotai"
import { Fragment } from "react"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"

type DiscreteParentNodeValues = (0 | 1)[]
type CategoricalParentNodeValues = string[]
type DeterministicParentNodeValues = (number | null)[]
type TableNodeValues = (number | string)[]

type DistributionParameterRecord = Record<string, (number | string | null)[]> // // lbound, loc, scale, ubound etc - number or node id
type DistributionRecord = { distribution: string[] } // e.g. ['Normal']

type ParentNodeRecord = Record<
  string,
  | DeterministicParentNodeValues
  | CategoricalParentNodeValues
  | DiscreteParentNodeValues
  | TableNodeValues
>
type EmptyRecord = Record<string, undefined>

type NodeDfCpd<TNodeRecord> = Partial<DistributionRecord & DistributionParameterRecord> &
  (ParentNodeRecord | EmptyRecord | TNodeRecord)

export type NodeRecord = Record<string, unknown>

export type NodeProperties<TNodeRecord extends NodeRecord, TSampleValue = string> = {
  parents_lst: (keyof ParentNodeRecord)[]
  a_sample_value: TSampleValue | null
  df_cpd: NodeDfCpd<TNodeRecord>
}

type PropertiesTableProps<
  TFieldValues extends FieldValues,
  TNodeRecord extends NodeRecord,
  TSampleValue = string,
> = {
  editable?: boolean
  labelMap: Record<string, string>
  nodeTypeMap: Record<string, NodeType | GraphNodeType>
  onBlur?: (field: string, newValue: string, index: number) => void
  customizeRow?: (row: PropertiesRow<TFieldValues>, rowIndex: number) => PropertiesRow<TFieldValues>
  customizeHeader?: (header: Header) => Header
  type: GraphNodeType | NodeType
  control?: Control<TFieldValues>
  name?: Path<TFieldValues>
  onChange?: (index: number, field: string, newValue: unknown) => void
  enableSelection?: boolean
  /** Errors rendered in a full-width row under their associated row. */
  validationErrors?: ValidationError[]
} & (
  | { properties: NodeProperties<TNodeRecord, TSampleValue>; headers?: never; rows?: never }
  | { properties?: never; headers: Header[]; rows: PropertiesRow<TFieldValues>[] }
)

export type PropertiesRow<TFieldValues extends FieldValues = FieldValues> = Record<
  string,
  EditableCellProps<TFieldValues>
>

const PropertiesTable = <
  TFieldValues extends FieldValues,
  TNodeRecord extends NodeRecord,
  TSampleValue = string,
>({
  properties,
  labelMap,
  nodeTypeMap,
  editable = false,
  onBlur,
  customizeRow,
  customizeHeader,
  type,
  control,
  name,
  enableSelection = false,
  headers: propsHeaders,
  rows: propsRows,
  validationErrors,
}: PropertiesTableProps<TFieldValues, TNodeRecord, TSampleValue>) => {
  const [selectedRowIndex, setSelectedRowIndex] = useAtom(selectedPropertiesRowAtom)

  const headers =
    propsHeaders ?? getPropertiesHeaders<TNodeRecord, TSampleValue>(properties, labelMap)
  const rows =
    propsRows ??
    getPropertiesRows<TFieldValues, TNodeRecord, TSampleValue>(
      properties,
      labelMap,
      nodeTypeMap,
      type,
      onBlur,
      editable,
    )

  const customizedHeaders = customizeHeader ? headers.map(customizeHeader) : headers
  const customizedRows = customizeRow ? rows.map(customizeRow) : rows

  const columnCount = customizedHeaders.length + (enableSelection ? 1 : 0)

  return (
    <TableContainer>
      <Table size="small" padding="none">
        <TableHead sx={{ backgroundColor: HIGHLIGHT_BACKGROUND }}>
          <TableRow>
            {enableSelection && <TableCell sx={{ width: "1%" }} />}
            {customizedHeaders.map((header) => (
              <EditableCell
                {...header}
                key={header.key}
                sx={{
                  paddingLeft: "0.3rem",
                  paddingRight: "0.2rem",
                }}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {customizedRows.map((row, i) => {
            const errors = validationErrors?.filter(({ meta }) => meta?.row === i) ?? []
            const errorColumns = new Set<string>(errors.flatMap(({ meta }) => meta?.col ?? []))
            const highlightSx =
              enableSelection && selectedRowIndex === i
                ? { backgroundColor: HIGHLIGHT_BACKGROUND }
                : undefined

            return (
              <Fragment key={i}>
                <TableRow
                  sx={{
                    ...highlightSx,
                    // errors row below belongs to this row - no line between them
                    ...(errors.length > 0 ? { "& td": { borderBottom: "none" } } : {}),
                  }}
                >
                  {enableSelection && (
                    <TableCell>
                      <Radio
                        size="small"
                        checked={selectedRowIndex === i}
                        onChange={() => setSelectedRowIndex(i)}
                      />
                    </TableCell>
                  )}
                  {headers.map(({ key }) => (
                    <EditableCell
                      key={key}
                      {...row[key]}
                      sx={{
                        paddingLeft: "0.3rem",
                        paddingRight: "0.2rem",
                        ...(row[key]?.sx ?? {}),
                        ...(errorColumns.has(key) ? ERROR_CELL_SX : {}),
                      }}
                      control={control}
                      name={name ? (`${name}.${i}.${key}` as Path<TFieldValues>) : undefined}
                      value={row[key]?.value ?? null}
                    />
                  ))}
                </TableRow>

                {errors.length > 0 && (
                  <TableRow sx={highlightSx}>
                    <TableCell
                      colSpan={columnCount}
                      sx={{
                        paddingLeft: "0.3rem",
                        paddingRight: "0.2rem",
                        paddingBottom: "0.2rem",
                      }}
                    >
                      {errors.map(({ msg }, errorIndex) => (
                        <FormHelperText
                          key={`${msg}-${errorIndex}`}
                          error
                          {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}
                        >
                          - {msg}
                        </FormHelperText>
                      ))}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default PropertiesTable

const HIGHLIGHT_BACKGROUND = "rgba(225, 225, 225, 0.4)"
const ERROR_CELL_SX = {
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #d32f2f",
  },
}
